import { Observable, timer, throwError } from 'rxjs';
import { mergeMap, finalize, } from 'rxjs/operators';
import { HttpErrorResponse, HttpRequest, HttpResponse } from '@angular/common/http';

export const BackoffRetryStrategy = ({
    maxRetryAttempts = 4,
    scalingDuration = 1000,
    excludeWhen = defaultExclusionFunc,
    request
} : {
    maxRetryAttempts?: number,
    scalingDuration?: number,
    excludeWhen?: (response: HttpResponse<any>, request?: HttpRequest<any>) => boolean,
    request?: HttpRequest<any>
} = {}) => (attempts: Observable<any>) => {
    return attempts.pipe(
        mergeMap((error, i) => {
            const retryAttempt = i + 1;
            // if maximum number of retries have been met
            // or response does not pass the excludeWhen function, throw error
            if (retryAttempt > maxRetryAttempts || excludeWhen(error, request)) {
                return throwError(() => error);
            }
            // Check for Retry-After header, otherwise use exponential backoff
            const retryAfterHeader = error.headers?.get('Retry-After');
            let delayMs: number;

            if (retryAfterHeader) {
                // Parse Retry-After header (can be seconds or HTTP date)
                const retryAfterSeconds = parseInt(retryAfterHeader, 10);
                if (!isNaN(retryAfterSeconds)) {
                    // Header contains seconds
                    delayMs = retryAfterSeconds * 1000;
                } else {
                    // Header contains HTTP date
                    const retryAfterDate = new Date(retryAfterHeader);
                    const now = new Date();
                    const calculatedDelay = retryAfterDate.getTime() - now.getTime();
                    // If date is invalid or parsing failed, fall back to exponential backoff
                    if (isNaN(calculatedDelay)) {
                        delayMs = retryAttempt * scalingDuration;
                    } else {
                        delayMs = Math.max(0, calculatedDelay);
                    }
                }
            } else {
                // Use exponential backoff: 1000ms, 2000ms, 3000ms, etc...
                delayMs = retryAttempt * scalingDuration;
            }

            console.log(
                `Attempt ${retryAttempt}: retrying in ${delayMs}ms`
            );
            return timer(delayMs);
        }),
        finalize(() => console.log('Retries are done!'))
    );
};

export const defaultExclusionFunc = (
    response: HttpResponse<any> | HttpErrorResponse,
    request?: HttpRequest<any>
): boolean => {
    // Handle both HttpResponse and HttpErrorResponse
    const status = (response as any)?.status;
        
    // TODO: Refactor header names to constants.  Angular converts all header names to lowercase.
    // This is why it's lowercase here when checking for existence, but not when it gets set.
    // Check if this is a WCF REST policy service request.  
    // All HTTP requests to WCF REST policy services is a POST.
    const isWCFRestPolicyService = request?.headers?.has('x-qs-resultsid') || 
                                   request?.headers?.has('x-qs-policyid');
    
    // Both GET requests and WCF REST policy services retry the same errors
    if (request?.method === 'GET' || isWCFRestPolicyService) {
        switch (status) {
            case 429: // "too many requests" -- indicates rate-limiting
            case 502: // "bad gateway" -- may indicate a transient infrastucture issue
            case 503: // "service unavailable" -- used by Underwriting when certain resources are busy (e.g.: DataStore)
            case 504: // "gateway timeout" -- may indicate a transient infrastucture issue
                return false;
            default:
                return true;
        }
    }
    
    // All other requests (POST, PUT, PATCH, DELETE without policy headers)
    switch (status) {
        case 429: // "too many requests" -- indicates rate-limiting
        case 503: // "service unavailable" -- used by Underwriting when certain resources are busy (e.g.: DataStore)
            return false;
        default:
            return true;
    }
};